/** The append-only audit log: who did what, when, from where. */
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Notice,
  Select,
  Skeleton,
  Spot,
  formatDateTime,
} from "@custos/ui";
import { useState } from "react";
import { Link } from "react-router";
import { PageHeader } from "../components/AdminShell";
import { errorMessage } from "../lib/errors";
import { useAudit } from "../lib/queries";
import { Pager } from "./Users";

export function AuditPage() {
  const [draft, setDraft] = useState({ actorType: "", action: "", target: "" });
  const [filter, setFilter] = useState({ actorType: "", action: "", target: "" });
  const [page, setPage] = useState(0);
  const audit = useAudit({
    ...(filter.actorType ? { actorType: filter.actorType } : {}),
    ...(filter.action ? { action: filter.action } : {}),
    ...(filter.target ? { target: filter.target } : {}),
    page,
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Every money-related and security action. Entries can never be edited or deleted."
      />
      <Card className="mb-4">
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,160px)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(0);
            setFilter({
              actorType: draft.actorType,
              action: draft.action.trim(),
              target: draft.target.trim(),
            });
          }}
        >
          <Field label="Who">
            {(p) => (
              <Select
                {...p}
                value={draft.actorType}
                onChange={(e) => setDraft({ ...draft, actorType: e.target.value })}
              >
                <option value="">Everyone</option>
                <option value="USER">Users</option>
                <option value="ADMIN">Admins</option>
                <option value="SYSTEM">System</option>
              </Select>
            )}
          </Field>
          <Field label="Action contains">
            {(p) => (
              <Input
                {...p}
                placeholder="withdrawal."
                value={draft.action}
                onChange={(e) => setDraft({ ...draft, action: e.target.value })}
              />
            )}
          </Field>
          <Field label="User / item id">
            {(p) => (
              <Input
                {...p}
                placeholder="uuid"
                value={draft.target}
                onChange={(e) => setDraft({ ...draft, target: e.target.value })}
              />
            )}
          </Field>
          <Button type="submit" variant="secondary">
            Filter
          </Button>
        </form>
      </Card>

      {audit.isPending ? (
        <Skeleton className="h-64 rounded-card" />
      ) : audit.error ? (
        <Notice tone="danger">{errorMessage(audit.error)}</Notice>
      ) : audit.data.events.length === 0 ? (
        <Card>
          <EmptyState icon={<Spot name="history" />} title="No matching events" />
        </Card>
      ) : (
        <>
          <ul className="flex flex-col gap-2" data-testid="audit-list">
            {audit.data.events.map((e) => (
              <li key={e.id} className="rounded-card bg-surface p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    tone={
                      e.actorType === "ADMIN" ? "primary" : e.actorType === "SYSTEM" ? "neutral" : "success"
                    }
                  >
                    {e.actorType.toLowerCase()}
                  </Badge>
                  <span className="font-mono text-sm text-fg">{e.action}</span>
                  <span className="ml-auto text-xs text-muted">{formatDateTime(e.createdAt)}</span>
                </div>
                <p className="mt-1 text-xs break-all text-muted">
                  {e.targetType && `${e.targetType} `}
                  {e.targetType === "user" && e.targetId ? (
                    <Link to={`/users/${e.targetId}`} className="text-fg hover:underline">
                      {e.targetId}
                    </Link>
                  ) : (
                    e.targetId
                  )}
                  {e.ip && ` · ${e.ip}`}
                  {e.actorId && ` · by ${e.actorId}`}
                </p>
                {e.metadata != null && (
                  <pre className="mt-2 max-h-40 overflow-auto rounded-field bg-soft p-2 font-mono text-[11px] whitespace-pre-wrap text-muted">
                    {JSON.stringify(e.metadata, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
          <Pager page={page} total={audit.data.total} pageSize={audit.data.pageSize} onPage={setPage} />
        </>
      )}
    </>
  );
}
